// react
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Step } from 'react-joyride';
import { useDispatch, useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { useSearch } from '@tanstack/react-router';

// material ui icons
import SettingsIcon from '@mui/icons-material/Settings';

// material ui
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';

// shared components
import { getDataViewerPageTour } from '@/components/joyride/DataViewerPage.tour';
import useAppTour from '@/components/joyride/useAppTour';
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';

// feature-hooks
import useDataViewerSettings from '@/features/data-viewer/hooks/useDataViewerSettings';
import useDateRange from '@/features/data-viewer/hooks/useDateRange';
import useLiveModeManager from '@/features/data-viewer/hooks/useLiveModeManager';
import useSelectedUnits from '@/features/data-viewer/hooks/useSelectedUnits';
import useSeriesVisibilityUrl from '@/features/data-viewer/hooks/useSeriesVisibilityUrl';

// components
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import AggregateTypeSelector from '../components/AggregateTypeSelector';
import DataViewerChart, {
  type DataViewerChartHandle,
} from '../components/DataViewerChart';
import DataViewerDateRangeSelector from '../components/DataViewerDateRangeSelector';
import DataViewerLegend from '../components/DataViewerLegend';
import DataViewerSettingsDialog from '../components/DataViewerSettingsDialog';
import UnitAutocomplete from '../components/UnitAutocomplete';
import useDataViewerData from '../hooks/useDataViewerData';
import type {
  AggregateType,
  ChartSeries,
  DateRange,
  GetAggregateTypeArgs,
} from '../types';

import { DateTime } from 'luxon';

// Aggregation logic
const AGGREGATE_OPTIONS: AggregateType[] = ['Raw', 'Minutes5', 'Hour', 'Day'];
const AGGREGATE_THRESHOLDS: Record<AggregateType, number> = {
  Raw: 0.25, // max days for Raw
  Minutes5: 7, // max days for 5 min
  Hour: 365, // max days for Hour (1 year)
  Day: Infinity, // max days for Day
};

function getAutoAggregateType(from: DateTime, to: DateTime): AggregateType {
  const diffDays = to.diff(from, 'days').days;
  if (diffDays <= AGGREGATE_THRESHOLDS.Raw) return 'Raw';
  if (diffDays <= AGGREGATE_THRESHOLDS.Minutes5) return 'Minutes5';
  if (diffDays <= AGGREGATE_THRESHOLDS.Hour) return 'Hour';
  return 'Day';
}

function getAggregateType({
  from,
  to,
  requested,
}: GetAggregateTypeArgs): AggregateType {
  const diffDays = to.diff(from, 'days').days;
  if (!requested) return getAutoAggregateType(from, to);
  if (requested === 'Raw' && diffDays > AGGREGATE_THRESHOLDS.Raw)
    return getAutoAggregateType(from, to);
  if (requested === 'Minutes5' && diffDays > AGGREGATE_THRESHOLDS.Minutes5)
    return getAutoAggregateType(from, to);
  if (requested === 'Hour' && diffDays > AGGREGATE_THRESHOLDS.Hour)
    return getAutoAggregateType(from, to);
  return requested;
}

const DataViewerPage = () => {
  const { t } = useTranslation('view-data');
  const dispatch = useDispatch();
  const search = useSearch({ strict: false });
  const hasAutoStartedTourRef = useRef(false);
  const dataViewerTourConfig: Parameters<typeof useAppTour>[0] = {
    steps: getDataViewerPageTour() as Step[],
    styles: {},
    floaterProps: {},
    options: {},
    tooltipComponent: null,
  };
  const { controls, Tour } = useAppTour(dataViewerTourConfig);
  const [aggregateOverride, setAggregateOverride] =
    useState<AggregateType | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, updateSettings] = useDataViewerSettings();
  // Zoom state stores the zoom range, the date range it was created for, and the
  // navigator series captured at zoom time. This lets zoomRange and navigatorSeries
  // both be derived during render — no effects needed.
  const [zoomState, setZoomState] = useState<{
    zoom: DateRange;
    fromIso: string;
    toIso: string;
    navigatorSeries: ChartSeries[];
  } | null>(null);
  // Sync selectedUnits, dateRange, and liveMode with URL query params (no redux)
  const accountId = useSelector(getSelectedAccount).AccountId;

  const chartRef = useRef<DataViewerChartHandle>(null);
  // Debounce ref for zoom/navigator events — prevents a new 30-series API fetch
  // on every position while the user is dragging the navigator handles.
  const zoomDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const isDataViewerTour =
      search?.tour === 'view-data' || search?.tour === 'data-viewer';

    if (isDataViewerTour && !hasAutoStartedTourRef.current) {
      hasAutoStartedTourRef.current = true;
      controls.start();
    } else if (!search?.tour) {
      controls.close();
    }
  }, [controls, search?.tour]);

  const [selectedUnits, setSelectedUnitIdsInUrl] = useSelectedUnits();
  const [dateRange, setDateRangeInUrlBase] = useDateRange();
  const [initialSeriesVisibility, setSeriesVisibilityInUrl] =
    useSeriesVisibilityUrl();

  // Zoom is only valid for the date range it was created for — derived during render
  const zoomRange =
    zoomState &&
    zoomState.fromIso === (dateRange.from?.toISO() ?? '') &&
    zoomState.toIso === (dateRange.to?.toISO() ?? '')
      ? zoomState.zoom
      : null;

  // Wrapper to validate aggregate override when date range changes
  const setDateRangeInUrl = (newRange: DateRange) => {
    setDateRangeInUrlBase(newRange);
    if (newRange.from && newRange.to) {
      chartRef.current?.resetExtremes(newRange.from, newRange.to);
    }

    // Check if current aggregate override is still valid for new date range
    if (aggregateOverride) {
      const validAggregateTypes = AGGREGATE_OPTIONS.filter(
        (opt) =>
          getAggregateType({
            from: newRange.from,
            to: newRange.to,
            requested: opt,
          }) === opt,
      );

      if (!validAggregateTypes.includes(aggregateOverride)) {
        setAggregateOverride(null);
        dispatch(
          showSnackbar({
            message: t('aggregate.invalid_selection_reset'),
            severity: 'info',
          }),
        );
      }
    }
  };

  const { liveMode, liveRangeMinutes, setLiveRangeMinutes, toggleLiveMode } =
    useLiveModeManager(dateRange, setDateRangeInUrl);

  const effectiveDateRange = zoomRange ?? dateRange;
  const effectiveAggregateOverride =
    aggregateOverride ??
    getAutoAggregateType(effectiveDateRange.from, effectiveDateRange.to);
  const availableAggregateTypes = useMemo(
    () =>
      zoomRange
        ? AGGREGATE_OPTIONS.filter(
            (opt) =>
              getAggregateType({
                from: zoomRange.from,
                to: zoomRange.to,
                requested: opt,
              }) === opt,
          )
        : AGGREGATE_OPTIONS.filter(
            (opt) =>
              getAggregateType({
                from: dateRange.from,
                to: dateRange.to,
                requested: opt,
              }) === opt,
          ),
    [dateRange.from, dateRange.to, zoomRange],
  );

  const {
    units,
    unitsLoading,
    chartSeries,
    isLoading,
    yAxes,
    seriesVisibility,
    setSeriesVisibility,
    expandedRecordedDataTypes,
  } = useDataViewerData({
    accountId,
    selectedUnits,
    dateRange: effectiveDateRange,
    liveMode,
    isAuto: !aggregateOverride,
    aggregateOverride: effectiveAggregateOverride,
    aggregateTypes: availableAggregateTypes,
    initialSeriesVisibility,
    showAllSeriesByDefault: settings.showAllSeriesByDefault,
  });

  const handleSettingsChange = (patch: Partial<typeof settings>) => {
    // Drop per-series overrides so the new default applies to every series
    if (patch.showAllSeriesByDefault !== undefined) {
      setSeriesVisibility({});
    }
    updateSettings(patch);
  };

  useEffect(() => {
    if (chartSeries.length === 0) return;

    const timeoutId = setTimeout(() => {
      const chartSeriesIds = new Set(chartSeries.map((series) => series.id));
      const hiddenSeriesIds: string[] = [];
      const visibleSeriesIds: string[] = [];

      Object.entries(seriesVisibility).forEach(([id, entry]) => {
        if (!chartSeriesIds.has(id)) return;
        if (entry.visible === false) hiddenSeriesIds.push(id);
        if (entry.visible === true) visibleSeriesIds.push(id);
      });

      setSeriesVisibilityInUrl({
        hiddenIds: hiddenSeriesIds,
        visibleIds: visibleSeriesIds,
      });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [chartSeries, seriesVisibility, setSeriesVisibilityInUrl]);

  // Pinned navigator series — when not zoomed, always reflects the current
  // full-range data; when zoomed, frozen at the series captured at zoom time.
  const navigatorSeries = zoomRange
    ? (zoomState?.navigatorSeries ?? [])
    : chartSeries;

  const handleZoom = (min: number, max: number) => {
    if (zoomDebounceRef.current !== null) {
      clearTimeout(zoomDebounceRef.current);
    }
    zoomDebounceRef.current = setTimeout(() => {
      zoomDebounceRef.current = null;
      const fromIso = dateRange.from?.toISO() ?? '';
      const toIso = dateRange.to?.toISO() ?? '';
      setZoomState((prev) => {
        // Only capture navigatorSeries on the first zoom for this date range.
        // When already zoomed on the same range, chartSeries is the zoomed data —
        // preserve the previously captured full-range navigator series instead.
        const alreadyZoomedOnThisRange =
          prev?.fromIso === fromIso && prev?.toIso === toIso;
        return {
          zoom: {
            from: DateTime.fromMillis(min),
            to: DateTime.fromMillis(max),
          },
          fromIso,
          toIso,
          navigatorSeries: alreadyZoomedOnThisRange
            ? prev!.navigatorSeries
            : chartSeries,
        };
      });
    }, 150);
  };

  const handleResetZoom = () => {
    setZoomState(null);
    chartRef.current?.resetExtremes(dateRange.from, dateRange.to);
  };

  return (
    <PageContainer>
      {Tour}
      <PageHeader title={t('title')} disableArrow>
        <Tooltip title={t('settings.title')}>
          <IconButton
            aria-label={t('settings.title')}
            onClick={() => setSettingsOpen(true)}
          >
            <SettingsIcon />
          </IconButton>
        </Tooltip>
      </PageHeader>
      <DataViewerSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={settings}
        onChange={handleSettingsChange}
      />

      <Card variant="outlined">
        <CardContent>
          <Box
            sx={{
              p: { xs: 1, md: 1.25 },
              borderRadius: 1.75,
              mb: 1.5,
            }}
          >
            <Box
              sx={{
                display: 'grid',
                rowGap: 1.25,
                columnGap: { xs: 5, lg: 10 },
                alignItems: 'start',
                gridTemplateColumns: {
                  xs: '1fr',
                  lg: 'minmax(360px, 1.35fr) minmax(340px, 1fr)',
                },
              }}
            >
              <Box data-tour="data-viewer-units" sx={{ minWidth: 0 }}>
                <UnitAutocomplete
                  loading={unitsLoading}
                  units={units}
                  value={selectedUnits || []}
                  onChange={(newValue) => {
                    setSelectedUnitIdsInUrl(newValue.map((u) => u.UnitId));
                    setZoomState(null);
                  }}
                />
              </Box>

              <Box
                sx={{
                  minWidth: 0,
                  width: { xs: '100%', lg: 'fit-content' },
                  justifySelf: { lg: 'end' },
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <DataViewerDateRangeSelector
                  containerDataTour="data-viewer-date-range"
                  rangeButtonDataTour="data-viewer-today"
                  liveButtonDataTour="data-viewer-live"
                  from={dateRange.from}
                  to={dateRange.to}
                  onRangeChange={(range) => {
                    setDateRangeInUrl({
                      from: range.from as DateRange['from'],
                      to: range.to as DateRange['to'],
                    });
                  }}
                  liveMode={liveMode}
                  liveRangeMinutes={liveRangeMinutes}
                  onLiveRangeChange={setLiveRangeMinutes}
                  onToggleLive={() => {
                    toggleLiveMode();
                    setZoomState(null);
                  }}
                />

                {!liveMode && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                    }}
                  >
                    {zoomRange && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleResetZoom}
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {t('reset_zoom', { defaultValue: 'Reset zoom' })}
                      </Button>
                    )}
                    <AggregateTypeSelector
                      buttonDataTour="data-viewer-aggregation"
                      aggregateOverride={aggregateOverride}
                      options={AGGREGATE_OPTIONS}
                      onChange={setAggregateOverride}
                      getAggregateType={getAggregateType}
                      dateRange={{ from: dateRange.from, to: dateRange.to }}
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              borderRadius: 1.75,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {!selectedUnits?.length && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                  pointerEvents: 'none',
                }}
              >
                <Typography variant="h6" color="text.secondary">
                  {t('no_units_selected')}
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  {t('select_units_hint')}
                </Typography>
              </Box>
            )}
            <DataViewerChart
              key={String(liveMode)}
              ref={chartRef}
              isLoading={isLoading}
              seriesVisibility={seriesVisibility}
              setSeriesVisibility={setSeriesVisibility}
              chartSeries={chartSeries}
              yAxes={yAxes}
              onAfterSetExtremes={handleZoom}
              navigatorSeries={navigatorSeries}
              dateRange={effectiveDateRange}
              selectedDateRange={dateRange}
              liveMode={liveMode}
              rawDataMode={effectiveAggregateOverride === 'Raw'}
              yAxisStartsAtZero={settings.yAxisStartsAtZero}
            />
          </Box>
          <DataViewerLegend
            selectedUnits={selectedUnits}
            onRemoveUnit={(unitId) => {
              setSelectedUnitIdsInUrl(
                selectedUnits
                  .filter((u) => u.UnitId !== unitId)
                  .map((u) => u.UnitId),
              );
              setZoomState(null);
            }}
            expandedRecordedDataTypes={expandedRecordedDataTypes}
            chartSeries={chartSeries}
            seriesVisibility={seriesVisibility}
            setSeriesVisibility={setSeriesVisibility}
            onSeriesHoverIn={(id) => chartRef.current?.highlightSeries(id)}
            onSeriesHoverOut={() => chartRef.current?.highlightSeries(null)}
          />
        </CardContent>
      </Card>
    </PageContainer>
  );
};

export default DataViewerPage;
